import { create } from 'zustand';
import { useAthleteStore } from './useAthleteStore';
import { toLogicDate } from '@/utils/date-utils';
import { MetabolicService } from '@ai-services/metabolic.service';
import { Event } from '@/types/zod.schemas';
import { DetailedTotals } from '@/shared/logic/metabolic/nutrition-compute';
import { supabase } from '@/supabaseClient';
import { MetabolicSimulatorV5 } from '@/shared/logic/metabolic/v5/simulator';
import { SimulationResultPoint } from '@/shared/logic/metabolic/v5/types';
import { SystemicPhaseResolver } from '@/shared/logic/metabolic/v5/resolvers/systemic-phase-resolver';

interface MetabolicDailySnapshot {
    nutrition: DetailedTotals & { peak_insulin: number };
    supplements: DetailedTotals;
    combined: DetailedTotals;
    expended: {
        bmr: number;
        neat: number;
        training: number;
        total: number;
    };
    balance: number;
    dayEvents: any[];
    targets: any;
    // Tri-Core Telemetry Extensions (Derived from MEV4)
    simulation: SimulationResultPoint[];
    currentActiveFuel?: any;
    glycogenCapacity?: { max: number; current: number; spilloverRisk: boolean };
    timeToDepletionMins?: number;
    systemicPhase?: { score: number; label: string; color: string; hoursFasted: number };
}

interface MetabolicState {
    dailySnapshot: MetabolicDailySnapshot;
    coachAdvice: string | null;
    isLoading: boolean;
    isCoachAnalyzing: boolean;
    adviceAttemptedFor: string | null;
    weightWarning: boolean;
}

interface MetabolicActions {
    calculateDailyBalance: (dateObj: Date, events: Event[]) => Promise<void>;
    fetchCoachAdvice: () => Promise<void>;
}

const emptyMetric = { total: 0, sources: [] };

export const useMetabolicStore = create<MetabolicState & MetabolicActions>((set, get) => ({
    dailySnapshot: {
        nutrition: { 
            calories: emptyMetric, protein: emptyMetric, carbs: emptyMetric, fat: emptyMetric, insulin_load: emptyMetric, peak_insulin: 0,
            micros: {} 
        },
        supplements: {
            calories: emptyMetric, protein: emptyMetric, carbs: emptyMetric, fat: emptyMetric, insulin_load: emptyMetric,
            micros: {}
        },
        combined: {
            calories: emptyMetric, protein: emptyMetric, carbs: emptyMetric, fat: emptyMetric, insulin_load: emptyMetric,
            micros: {}
        },
        expended: { bmr: 0, neat: 0, training: 0, total: 0 },
        balance: 0,
        dayEvents: [],
        targets: { macros: { protein: 150, fiber: 30, insulin_load: 150 }, micros: {} },
        simulation: [],
        currentActiveFuel: { carbs: 0, glucose: 0, fructose: 0, complex: 0, protein: 0, fat: 0, gastricLoad: 0 },
        glycogenCapacity: { max: 500, current: 0, spilloverRisk: false },
        timeToDepletionMins: 0
    },
    coachAdvice: null,
    isLoading: false,
    isCoachAnalyzing: false,
    adviceAttemptedFor: null,
    weightWarning: false,

    calculateDailyBalance: async (dateObj, events) => {
        const dateStr = toLogicDate(dateObj);
        const { profile } = useAthleteStore.getState();

        // 1. DYNAMIC WEIGHT RESOLUTION
        let resolvedWeight = profile?.baseline_weight_kg || null;
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const { data: latestWeight } = await supabase
                    .schema('v3')
                    .from('health_metrics' as any)
                    .select('value')
                    .eq('metric_type', 'BODYWEIGHT')
                    .eq('user_id', session.user.id)
                    .order('measured_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (latestWeight?.value) resolvedWeight = Number(latestWeight.value);
            }
        } catch (e) {
            console.error("[MetabolicStore] Weight fetch failed:", e);
        }

        const isProfileHydrated = !!profile?.id;
        const hasNoWeight = isProfileHydrated && !resolvedWeight;
        const currentWeight = resolvedWeight || 85; 
        set({ weightWarning: hasNoWeight });

        // 2. JIT HYDRATION: Supplements
        const hydratedEvents = await Promise.all(events.map(async (event) => {
            if (event.type !== 'SUPPLEMENTATION' && event.type !== 'SUPPLEMENT') return event;
            if (!event.payload?.items) return event;
            const hydratedItems = await Promise.all(event.payload.items.map(async (item: any) => {
                if (item.baseline_micros) return item; 
                const { data } = await supabase.schema('v3').from('supplement_catalog').select('baseline_micros, measure_unit, manufacturer_scoop_weight').eq('product_name', item.item_name).maybeSingle();
                return { ...item, baseline_micros: data?.baseline_micros, manufacturer_scoop_weight: data?.manufacturer_scoop_weight, measure_unit: data?.measure_unit };
            }));
            return { ...event, payload: { ...event.payload, items: hydratedItems } };
        }));

        // 2.5 STATEFUL GLYCOGEN ACCUMULATOR (7-Day Lookback)
        // Calculates the carrying debt from the previous week to solve the "48-Hour Amnesia" problem.
        const lbmForAccumulator = currentWeight * (1 - ((profile?.body_fat_percentage || 15) / 100));
        const maxGlycogenForAccumulator = Math.round(lbmForAccumulator * 15);
        
        let rollingDebt = 0;
        const startOfTarget = new Date(dateObj).setHours(0,0,0,0);
        const sevenDaysAgo = startOfTarget - (7 * 86400000);

        // Sort chronologically to simulate time passing
        const historicalEvents = events
            .filter(e => {
                const t = new Date(e.start_time).getTime();
                return t >= sevenDaysAgo && t < startOfTarget;
            })
            .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

        // Apply a daily basal drain. Even without working out, the brain (liver glycogen) 
        // and incidental movement (NEAT) slowly deplete total systemic glycogen reserves.
        // Approx 60g per day basal drain.
        const daysPassed = Math.max(1, (startOfTarget - sevenDaysAgo) / 86400000);
        rollingDebt += (60 * daysPassed);

        historicalEvents.forEach(e => {
            const type = e.type?.toUpperCase();
            if (type === 'PERFORMANCE' || type === 'WORKOUT') {
                const rpe = Number(e.payload?.rpe || 7);
                const start = new Date(e.start_time).getTime();
                const end = new Date(e.end_time || start + 3600000).getTime();
                const durationMins = (end - start) / 60000;
                // High Intensity (1.0) = ~0.02g burn per kg LBM per min
                const burn = (rpe / 10) * (lbmForAccumulator * 0.02) * durationMins;
                rollingDebt += burn;
            } else if (type === 'NUTRITION' || type === 'NUTRITION_LOG') {
                const carbs = Number(e.payload?.carbs?.total || e.payload?.carbs || 0);
                // Assume ~80% of net carbs clear into the muscle sink eventually
                rollingDebt -= (carbs * 0.8);
            }
            if (rollingDebt < 0) rollingDebt = 0;
            if (rollingDebt > maxGlycogenForAccumulator) rollingDebt = maxGlycogenForAccumulator;
        });

        const profileWithState = { ...profile, initialGlycogenDebt: rollingDebt };

        // 3. CORE SIMULATION (MEV5 CLEAN ROOM)
        const simulation = MetabolicSimulatorV5.simulate(hydratedEvents, dateStr, profileWithState);
        
        // 4. TELEMETRY DERIVATION (Current Snapshot)
        const now = new Date();
        const isToday = toLogicDate(dateObj) === toLogicDate(now);
        
        // If today, show the live exact minute. If past/future, show the End of Day (23:59) net result.
        const currentMinsPassed = isToday ? (now.getHours() * 60) + now.getMinutes() : 1439;

        let closest = simulation[0];
        let minDiff = Infinity;
        simulation.forEach(p => {
            const diff = Math.abs(p.minute - currentMinsPassed);
            if (diff < minDiff) { minDiff = diff; closest = p; }
        });

        const activeState = closest.state;
        const activeFuel = {
            glucose: activeState?.fractions?.glucose?.blood || 0,
            fructose: activeState?.fractions?.fructose?.blood || 0,
            complex: activeState?.fractions?.complex_carbs?.blood || 0,
            carbs: (activeState?.fractions?.glucose?.blood || 0) + (activeState?.fractions?.fructose?.blood || 0) + (activeState?.fractions?.complex_carbs?.blood || 0),
            protein: (activeState?.fractions?.intact_protein?.blood || 0) + (activeState?.fractions?.free_amino_acids?.blood || 0),
            fat: activeState?.fractions?.lipids?.blood || 0,
            gastricLoad: activeState?.gastricVolume || 0,
            insulin: activeState?.insulin || 0
        };

        // 5. EXPENDITURE & BALANCE
        const nutrition = MetabolicService.aggregateDayNutrition(hydratedEvents, dateStr);
        const supplements = MetabolicService.aggregateDaySupplements(hydratedEvents, dateStr);
        const combined = MetabolicService.mergeMetabolicLoad(nutrition, supplements);
        const trainingBurn = MetabolicService.aggregateTrainingBurn(hydratedEvents, dateStr);
        const targets = MetabolicService.calculateDynamicTargets(profile, trainingBurn, nutrition.calories.total);

        const bmr = profile?.birth_date 
            ? MetabolicService.calculateBMR(profile.gender, profile.height_cm, currentWeight, profile.birth_date, profile.body_fat_percentage)
            : 1800;
        const neat = Math.round(bmr * ((profile?.activity_multiplier || 1.2) - 1));
        const totalExpended = bmr + neat + trainingBurn;

        // 6. GLYCOGEN SINK (MEV5.3 Digital Twin Integration)
        const lbm = currentWeight * (1 - ((profile?.body_fat_percentage || 15) / 100));
        const maxGlycogen = Math.round(lbm * 15);
        
        // The V5 engine calculates 'Debt' (0 = full, max = empty). 
        // The UI shows 'Current Level' (max = full, 0 = empty).
        const currentGlycogen = Math.max(0, maxGlycogen - activeState.glycogenDebt);
        const burnRatePerMin = bmr / 1440;

        // 7. SYSTEMIC PHASE RESOLVER (Endocrine State Machine)
        // Biological Fasting Clock: Time since insulin was last elevated (> 10.0 uU/mL)
        let lastInsulinSpikeMin = -1440; // Default to start of simulation (Yesterday Midnight)
        
        simulation.forEach(p => {
            if (p.minute <= currentMinsPassed && p.state.insulin > 10.0) {
                lastInsulinSpikeMin = p.minute;
            }
        });

        const hoursFasted = Math.max(0, (currentMinsPassed - lastInsulinSpikeMin) / 60);
        const systemicPhase = SystemicPhaseResolver.compute(activeState.insulin || 0, activeState.glycogenDebt || 0, hoursFasted);

        set({
            dailySnapshot: {
                nutrition: { ...nutrition, peak_insulin: Math.max(...simulation.map(p => p.state.insulin)) },
                supplements, combined, expended: { bmr, neat, training: trainingBurn, total: totalExpended },
                balance: totalExpended - nutrition.calories.total,
                dayEvents: hydratedEvents.filter(e => toLogicDate(e.start_time) === dateStr),
                targets, simulation, currentActiveFuel: activeFuel,
                glycogenCapacity: { max: maxGlycogen, current: currentGlycogen, spilloverRisk: currentGlycogen >= maxGlycogen * 0.95 },
                timeToDepletionMins: Math.round((activeFuel.carbs * 4) / burnRatePerMin),
                systemicPhase: { ...systemicPhase, hoursFasted }
            }
        });
    },

    fetchCoachAdvice: async () => {
        const { dailySnapshot } = get();
        const { profile } = useAthleteStore.getState();
        if (!profile) return;
        set({ isCoachAnalyzing: true });
        try {
            const dateStr = get().dailySnapshot.dayEvents[0] ? toLogicDate(get().dailySnapshot.dayEvents[0].start_time) : toLogicDate(new Date());
            set({ adviceAttemptedFor: dateStr });
            const { data, error } = await MetabolicService.getTacticalNutritionTip(dailySnapshot as any) as any;
            set({ coachAdvice: error ? "AI Coach cooling down." : data });
        } finally {
            set({ isCoachAnalyzing: false });
        }
    }
}));
